import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { resources } from '../../core/services/resources';
import { NewReportTemplateDialog } from './NewReportTemplateDialog';

interface ReportTemplateSwitcherProps {
  fullWidth?: boolean;
  /**
   * Active LOB id. The dropdown lists only templates whose `lineOfBizId`
   * matches it. When `null` the trigger is disabled — there's no LOB
   * context to scope to.
   */
  lineOfBizId: number | null;
}

/**
 * Sidebar selector for a report template scoped to the active LOB.
 * Selecting an entry navigates to that template's detail view (Prompts /
 * Prompt sets / Preview / Reports tabs). The full tenant catalog is
 * fetched once (shared cache with the list page) and filtered to the
 * active LOB client-side so changing LOB doesn't trigger a refetch.
 */
export function ReportTemplateSwitcher({ fullWidth, lineOfBizId }: ReportTemplateSwitcherProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Drive the "active" state from any /report-templates/:id/* URL so the
  // switcher stays in sync when the user is on Settings, Prompts, or
  // Instances. The wildcard match makes the picker work across all three
  // sub-sections without separate plumbing.
  const match = useMatch('/report-templates/:id/*');
  const activeId = match?.params.id ? Number(match.params.id) : null;

  // Preserve the current sub-section ("settings" / "prompts" / "instances")
  // when switching to a different template via the dropdown — clicking
  // another template while on Instances lands on the new template's
  // Instances, not its Prompts default.
  const currentSubsection = useMemo<'settings' | 'prompts' | 'instances'>(() => {
    if (pathname.endsWith('/settings')) return 'settings';
    if (pathname.endsWith('/instances')) return 'instances';
    return 'prompts';
  }, [pathname]);

  // Tenant-scoped (via JWT). Same cache key the list page already
  // populates, so this typically hits without a network round-trip.
  const templatesQuery = useQuery({
    queryKey: ['report-templates', 'select'],
    queryFn: () => resources.reportTemplates.list({ pageSize: 200, sort: 'name' }),
  });
  const allTemplates = templatesQuery.data?.data ?? [];

  // Filter the catalog by the active LOB. Done client-side so switching
  // LOB is instant and shares the underlying fetch with the list page.
  const templates = useMemo(
    () =>
      lineOfBizId == null
        ? []
        : allTemplates.filter((t) => t.lineOfBizId === lineOfBizId),
    [allTemplates, lineOfBizId],
  );

  // Trigger label: the URL-matched template only wins when it belongs
  // to the active LOB — otherwise the dropdown would still advertise a
  // template from a different LOB after the user switches scope. With
  // no in-LOB match we fall back to the first template for the active
  // LOB. Label-only; we never auto-navigate.
  const activeName = useMemo(() => {
    if (lineOfBizId == null) return 'Select line of business first';
    if (templatesQuery.isLoading) return 'Loading…';
    if (templates.length === 0) return 'No report templates';
    if (activeId != null) {
      const match = templates.find((t) => t.id === activeId);
      if (match) return match.name;
    }
    return templates[0].name;
  }, [activeId, templates, templatesQuery.isLoading, lineOfBizId]);

  const open = Boolean(anchor);

  return (
    <>
      <Button
        size="small"
        fullWidth={fullWidth}
        disabled={lineOfBizId == null}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        startIcon={<AssessmentIcon fontSize="small" />}
        variant="outlined"
        sx={{
          textTransform: 'none',
          minWidth: 200,
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="body2" fontWeight={600} noWrap>
          {activeName}
        </Typography>
      </Button>

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 420 } } }}
      >
        {templatesQuery.isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Loading report templates…
            </Typography>
          </MenuItem>
        )}
        {!templatesQuery.isLoading && templates.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No report templates for this line of business
            </Typography>
          </MenuItem>
        )}
        {templates.map((t) => {
          const isActive = t.id === activeId;
          return (
            <MenuItem
              key={t.id}
              selected={isActive}
              onClick={() => {
                setAnchor(null);
                navigate(`/report-templates/${t.id}/${currentSubsection}`);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography
                  variant="body2"
                  fontWeight={isActive ? 600 : 400}
                  sx={{ flexGrow: 1 }}
                  noWrap
                >
                  {t.name}
                </Typography>
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </MenuItem>
          );
        })}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setCreateOpen(true);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" color="primary" fontWeight={600}>
            New report template
          </Typography>
        </MenuItem>
      </Menu>

      <NewReportTemplateDialog
        open={createOpen}
        defaultLineOfBizId={lineOfBizId}
        onClose={() => setCreateOpen(false)}
        onCreated={(tpl) => {
          // Jump straight into the new template's prompts view — that's
          // the most useful default landing page (no settings/instances
          // exist yet on a brand-new template).
          navigate(`/report-templates/${tpl.id}/prompts`);
        }}
      />
    </>
  );
}
